import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import Layout from '../../components/layout/main';
import {
    Box,
    Button,
    Card,
    CardActionArea,
    Chip,
    Fade,
    Grid,
    LinearProgress,
    Paper,
    Skeleton,
    Stack,
    Typography,
} from '@mui/material';
import { alpha, keyframes, useTheme } from '@mui/material/styles';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded';
import BiotechRoundedIcon from '@mui/icons-material/BiotechRounded';
import CategoryRoundedIcon from '@mui/icons-material/CategoryRounded';
import EditNoteRoundedIcon from '@mui/icons-material/EditNoteRounded';
import GroupsRoundedIcon from '@mui/icons-material/GroupsRounded';
import ReceiptLongRoundedIcon from '@mui/icons-material/ReceiptLongRounded';
import TagRoundedIcon from '@mui/icons-material/TagRounded';
import TipsAndUpdatesRoundedIcon from '@mui/icons-material/TipsAndUpdatesRounded';
import { AuthContext } from '../../context/auth';
import { getDataFull } from '../../services/ajax';

const floatSlow = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-8px); }
`;

const shimmer = keyframes`
  0% { background-position: 0% 50%; }
  100% { background-position: 200% 50%; }
`;

const pulseSoft = keyframes`
  0%, 100% { box-shadow: 0 0 0 0 rgba(13, 148, 136, 0.25); }
  50% { box-shadow: 0 0 28px 6px rgba(255, 107, 92, 0.18); }
`;

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(12px); }
  to { opacity: 1; transform: translateY(0); }
`;

const PASOS_USO = [
    {
        n: '1',
        titulo: 'Liquidación',
        texto: 'En NHPdGD elegís los estudios hechos y cuántas veces. El sistema arma el código y el monto de la liquidación.',
        icon: ReceiptLongRoundedIcon,
        color: '#0d9488',
    },
    {
        n: '2',
        titulo: 'Catálogo al día',
        texto: 'Los códigos del nomenclador y las determinaciones (qué incluye cada estudio) alimentan al generador. Si algo cambió en el laboratorio, actualizalo acá.',
        icon: EditNoteRoundedIcon,
        color: '#0d4f5c',
    },
    {
        n: '3',
        titulo: 'Listo para la próxima vez',
        texto: 'Volvé al inicio cuando quieras: el resumen de abajo te muestra si hay datos cargados para trabajar con tranquilidad.',
        icon: AutoAwesomeRoundedIcon,
        color: '#c2410c',
    },
];

function fmtNum(n) {
    if (n == null || Number.isNaN(n)) return '—';
    return Number(n).toLocaleString('es-AR');
}

export default function Page() {
    const theme = useTheme();
    const { usuario } = useContext(AuthContext);

    const isAdminVisible = useMemo(() => {
        if (!usuario) return false;
        return usuario.nivel_usuario === 1 || usuario.sup_usuarios === true;
    }, [usuario]);

    const [stats, setStats] = useState({
        sectores: null,
        codigos: null,
        determinaciones: null,
        usuarios: null,
    });
    const [statsLoading, setStatsLoading] = useState(true);
    const [statsError, setStatsError] = useState(false);

    useEffect(() => {
        let cancel = false;
        const token = usuario?.token;
        if (!token) {
            setStatsLoading(false);
            return undefined;
        }

        (async () => {
            setStatsLoading(true);
            setStatsError(false);

            const req = (path) => getDataFull(`${path}?per_page=1&page=1`, token);

            const [g, c, d, u] = await Promise.all([
                req('determinacion-grupos'),
                req('codigos'),
                req('determinaciones'),
                req('usuarios'),
            ]);

            if (cancel) return;

            const next = {
                sectores: g.error === '' && g.meta?.total != null ? g.meta.total : null,
                codigos: c.error === '' && c.meta?.total != null ? c.meta.total : null,
                determinaciones: d.error === '' && d.meta?.total != null ? d.meta.total : null,
                usuarios: u.error === '' && u.meta?.total != null ? u.meta.total : null,
            };

            if ([g.error, c.error, d.error, u.error].some((e) => e && e !== '')) {
                setStatsError(true);
            }

            setStats(next);
            setStatsLoading(false);
        })();

        return () => {
            cancel = true;
        };
    }, [usuario?.token]);

    const bgMesh = useMemo(
        () =>
            theme.palette.mode === 'dark'
                ? `radial-gradient(ellipse 100% 80% at 0% -20%, ${alpha('#ff6b5c', 0.14)} 0%, transparent 55%),
                   radial-gradient(ellipse 90% 70% at 100% 0%, ${alpha('#0d9488', 0.2)} 0%, transparent 50%),
                   linear-gradient(165deg, #0c1929 0%, #0a1628 45%, #0d1f35 100%)`
                : `radial-gradient(ellipse 100% 70% at 0% 0%, ${alpha('#ff6b5c', 0.12)} 0%, transparent 55%),
                   radial-gradient(ellipse 80% 60% at 100% 0%, ${alpha('#0d9488', 0.14)} 0%, transparent 48%),
                   linear-gradient(180deg, #f7f4ef 0%, #eef6f4 40%, #f0f7f5 100%)`,
        [theme.palette.mode]
    );

    const tarjetasDatos = [
        {
            key: 'sectores',
            label: 'Sectores',
            descripcion: 'Áreas o rubros de estudios',
            valor: stats.sectores,
            to: '/determinacion-grupo',
            icon: CategoryRoundedIcon,
            color: theme.palette.primary.main,
        },
        {
            key: 'codigos',
            label: 'Códigos',
            descripcion: 'Prácticas del nomenclador',
            valor: stats.codigos,
            to: '/codigo',
            icon: TagRoundedIcon,
            color: '#0d9488',
        },
        {
            key: 'determinaciones',
            label: 'Determinaciones',
            descripcion: 'Estudios armados para liquidar',
            valor: stats.determinaciones,
            to: '/determinacion',
            icon: BiotechRoundedIcon,
            color: '#0d4f5c',
        },
        ...(isAdminVisible
            ? [
                  {
                      key: 'usuarios',
                      label: 'Usuarios',
                      descripcion: 'Quién puede entrar al sistema',
                      valor: stats.usuarios,
                      to: '/usuario',
                      icon: GroupsRoundedIcon,
                      color: '#c2410c',
                  },
              ]
            : []),
    ];

    return (
        <Layout nombrePagina="Panel de inicio">
            <Box
                sx={{
                    fontFamily: '"DM Sans", system-ui, sans-serif',
                    borderRadius: 3,
                    p: { xs: 2, md: 3 },
                    mb: 2,
                    background: bgMesh,
                    border: `1px solid ${alpha(theme.palette.divider, 0.35)}`,
                    position: 'relative',
                    overflow: 'hidden',
                    '&::after': {
                        content: '""',
                        position: 'absolute',
                        inset: 0,
                        background: 'linear-gradient(110deg, transparent 40%, rgba(255,255,255,0.05) 50%, transparent 60%)',
                        backgroundSize: '200% 100%',
                        animation: `${shimmer} 10s linear infinite`,
                        pointerEvents: 'none',
                    },
                }}
            >
                <Stack spacing={3} sx={{ position: 'relative', zIndex: 1 }}>
                    {/* Hero — foco NHPdGD */}
                    <Paper
                        elevation={0}
                        sx={{
                            p: { xs: 2.5, md: 3.5 },
                            borderRadius: 3,
                            background:
                                theme.palette.mode === 'dark'
                                    ? `linear-gradient(135deg, ${alpha('#0d9488', 0.22)} 0%, ${alpha('#0d4f5c', 0.35)} 50%, ${alpha('#1e293b', 0.6)} 100%)`
                                    : `linear-gradient(135deg, ${alpha('#ecfdf5', 0.95)} 0%, ${alpha('#f0fdfa', 0.9)} 45%, ${alpha('#fff7ed', 0.85)} 100%)`,
                            border: `1px solid ${alpha('#0d9488', 0.35)}`,
                            animation: `${pulseSoft} 5s ease-in-out infinite`,
                        }}
                    >
                        <Stack
                            direction={{ xs: 'column', md: 'row' }}
                            spacing={3}
                            alignItems={{ md: 'center' }}
                            justifyContent="space-between"
                        >
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{ mb: 1 }}>
                                    <Chip
                                        label="Lo más usado"
                                        size="small"
                                        sx={{
                                            fontWeight: 700,
                                            bgcolor: alpha('#0d9488', 0.25),
                                            color: 'primary.dark',
                                            border: `1px solid ${alpha('#0d9488', 0.4)}`,
                                        }}
                                    />
                                    <Typography variant="caption" color="text.secondary">
                                        No guarda datos · solo referencia
                                    </Typography>
                                </Stack>
                                <Typography
                                    variant="h4"
                                    sx={{
                                        fontFamily: '"Fraunces", Georgia, serif',
                                        fontWeight: 700,
                                        letterSpacing: '-0.02em',
                                        lineHeight: 1.2,
                                        mb: 1,
                                    }}
                                >
                                    Generador NHPdGD
                                </Typography>
                                <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 560, lineHeight: 1.65 }}>
                                    Es la herramienta principal: marcás los estudios realizados y obtenés el texto de liquidación y el arancel estimado. El resto
                                    del menú sirve para <strong>mantener actualizado</strong> el catálogo que usa este generador.
                                </Typography>
                            </Box>
                            <Stack alignItems={{ xs: 'stretch', md: 'center' }} sx={{ flexShrink: 0 }}>
                                <Box
                                    sx={{
                                        animation: `${floatSlow} 4.5s ease-in-out infinite`,
                                        display: { xs: 'none', sm: 'block' },
                                        mb: { md: 0 },
                                    }}
                                >
                                    <ReceiptLongRoundedIcon sx={{ fontSize: 72, color: 'primary.main', opacity: 0.9 }} />
                                </Box>
                                <Button
                                    component={Link}
                                    to="/nhpdgd"
                                    variant="contained"
                                    size="large"
                                    endIcon={<ArrowForwardRoundedIcon />}
                                    sx={{
                                        py: 1.5,
                                        px: 3,
                                        borderRadius: 2,
                                        fontWeight: 700,
                                        fontSize: '1rem',
                                        boxShadow: `0 8px 24px ${alpha('#0d4f5c', 0.25)}`,
                                    }}
                                >
                                    Ir al generador
                                </Button>
                            </Stack>
                        </Stack>
                    </Paper>

                    {/* Guía en 3 pasos */}
                    <Box>
                        <Typography
                            variant="h6"
                            sx={{
                                fontFamily: '"Fraunces", Georgia, serif',
                                fontWeight: 700,
                                mb: 0.5,
                            }}
                        >
                            Cómo se usa el sistema
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5, maxWidth: 720 }}>
                            Tres ideas simples. Seguí el orden que mejor te quede; lo importante es tener el catálogo coherente
                            con lo que se factura.
                        </Typography>

                        <Grid container spacing={2}>
                            {PASOS_USO.map((paso, i) => {
                                const Icon = paso.icon;
                                return (
                                    <Grid key={paso.n} size={{ xs: 12, md: 4 }}>
                                        <Fade in timeout={400 + i * 120}>
                                            <Card
                                                variant="outlined"
                                                sx={{
                                                    height: '100%',
                                                    borderRadius: 2.5,
                                                    borderColor: alpha(paso.color, 0.35),
                                                    background: alpha(theme.palette.background.paper, theme.palette.mode === 'dark' ? 0.4 : 0.85),
                                                    animation: `${fadeUp} 0.6s ease-out ${i * 0.08}s both`,
                                                    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                                                    '&:hover': {
                                                        transform: 'translateY(-4px)',
                                                        boxShadow: `0 12px 28px ${alpha(paso.color, 0.12)}`,
                                                    },
                                                }}
                                            >
                                                {i < 2 ? (
                                                    <CardActionArea
                                                        component={Link}
                                                        to={i === 0 ? '/nhpdgd' : '/determinacion'}
                                                        sx={{ p: 2, height: '100%', alignItems: 'stretch' }}
                                                    >
                                                        <Stack spacing={1.25}>
                                                            <Stack direction="row" alignItems="center" spacing={1.5}>
                                                                <Box
                                                                    sx={{
                                                                        width: 40,
                                                                        height: 40,
                                                                        borderRadius: '50%',
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        justifyContent: 'center',
                                                                        fontWeight: 800,
                                                                        fontSize: '1rem',
                                                                        color: '#fff',
                                                                        bgcolor: paso.color,
                                                                        flexShrink: 0,
                                                                    }}
                                                                >
                                                                    {paso.n}
                                                                </Box>
                                                                <Icon sx={{ fontSize: 28, color: paso.color }} />
                                                            </Stack>
                                                            <Typography fontWeight={800} variant="subtitle1">
                                                                {paso.titulo}
                                                            </Typography>
                                                            <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.55 }}>
                                                                {paso.texto}
                                                            </Typography>
                                                            <Typography variant="caption" color="primary" fontWeight={700}>
                                                                Tocá para ir →
                                                            </Typography>
                                                        </Stack>
                                                    </CardActionArea>
                                                ) : (
                                                    <Box sx={{ p: 2, height: '100%' }}>
                                                        <Stack spacing={1.25}>
                                                            <Stack direction="row" alignItems="center" spacing={1.5}>
                                                                <Box
                                                                    sx={{
                                                                        width: 40,
                                                                        height: 40,
                                                                        borderRadius: '50%',
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        justifyContent: 'center',
                                                                        fontWeight: 800,
                                                                        fontSize: '1rem',
                                                                        color: '#fff',
                                                                        bgcolor: paso.color,
                                                                        flexShrink: 0,
                                                                    }}
                                                                >
                                                                    {paso.n}
                                                                </Box>
                                                                <Icon sx={{ fontSize: 28, color: paso.color }} />
                                                            </Stack>
                                                            <Typography fontWeight={800} variant="subtitle1">
                                                                {paso.titulo}
                                                            </Typography>
                                                            <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.55 }}>
                                                                {paso.texto}
                                                            </Typography>
                                                        </Stack>
                                                    </Box>
                                                )}
                                            </Card>
                                        </Fade>
                                    </Grid>
                                );
                            })}
                        </Grid>
                    </Box>

                    {/* Resumen de datos cargados */}
                    <Box>
                        <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1} sx={{ mb: 1.5 }}>
                            <Typography
                                variant="h6"
                                sx={{
                                    fontFamily: '"Fraunces", Georgia, serif',
                                    fontWeight: 700,
                                }}
                            >
                                Datos en el sistema
                            </Typography>
                            {statsLoading && <LinearProgress sx={{ flex: 1, minWidth: 120, maxWidth: 200, borderRadius: 1 }} />}
                        </Stack>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2, maxWidth: 800 }}>
                            Números orientativos: indican cuánto hay cargado para que el generador trabaje con información completa. Si un valor es bajo y
                            se esperan más estudios, revisá el catálogo.
                        </Typography>
                        {statsError && (
                            <Chip
                                icon={<TipsAndUpdatesRoundedIcon />}
                                label="No se pudieron cargar algunos totales. Revisá la conexión e intentá de nuevo."
                                color="warning"
                                variant="outlined"
                                sx={{ mb: 2 }}
                            />
                        )}

                        <Grid container spacing={2}>
                            {tarjetasDatos.map((t) => {
                                const Icon = t.icon;
                                return (
                                    <Grid key={t.key} size={{ xs: 12, sm: 6, md: 3 }}>
                                        <Card
                                            component={Link}
                                            to={t.to}
                                            variant="outlined"
                                            sx={{
                                                height: '100%',
                                                borderRadius: 2,
                                                textDecoration: 'none',
                                                color: 'inherit',
                                                borderColor: alpha(t.color, 0.25),
                                                background: alpha(theme.palette.background.paper, theme.palette.mode === 'dark' ? 0.35 : 0.9),
                                                transition: 'all 0.2s ease',
                                                '&:hover': {
                                                    borderColor: alpha(t.color, 0.55),
                                                    transform: 'translateY(-2px)',
                                                    boxShadow: `0 8px 20px ${alpha(t.color, 0.12)}`,
                                                },
                                            }}
                                        >
                                            <Box sx={{ p: 2 }}>
                                                <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
                                                    <Box>
                                                        <Typography variant="caption" color="text.secondary" fontWeight={700} letterSpacing={0.03}>
                                                            {t.label}
                                                        </Typography>
                                                        <Typography variant="caption" display="block" color="text.secondary" sx={{ opacity: 0.85, mt: 0.25 }}>
                                                            {t.descripcion}
                                                        </Typography>
                                                    </Box>
                                                    <Box
                                                        sx={{
                                                            p: 0.75,
                                                            borderRadius: 1.5,
                                                            bgcolor: alpha(t.color, 0.12),
                                                            color: t.color,
                                                        }}
                                                    >
                                                        <Icon fontSize="small" />
                                                    </Box>
                                                </Stack>
                                                <Typography
                                                    variant="h4"
                                                    sx={{
                                                        fontFamily: '"Fraunces", Georgia, serif',
                                                        fontWeight: 700,
                                                        mt: 1.5,
                                                        color: 'text.primary',
                                                    }}
                                                >
                                                    {statsLoading ? <Skeleton width={72} /> : fmtNum(t.valor)}
                                                </Typography>
                                                <Typography variant="caption" color="primary" fontWeight={700} sx={{ mt: 0.5, display: 'inline-block' }}>
                                                    Abrir módulo →
                                                </Typography>
                                            </Box>
                                        </Card>
                                    </Grid>
                                );
                            })}
                        </Grid>
                    </Box>

                    {/* Cierre amable */}
                    <Paper
                        elevation={0}
                        sx={{
                            p: 2,
                            borderRadius: 2,
                            bgcolor: alpha(theme.palette.info.main, theme.palette.mode === 'dark' ? 0.12 : 0.08),
                            border: `1px dashed ${alpha(theme.palette.info.main, 0.35)}`,
                        }}
                    >
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }}>
                            <TipsAndUpdatesRoundedIcon color="info" />
                            <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                                <strong>Tip:</strong> si el laboratorio actualizó precios o prácticas, cargá primero los códigos y las determinaciones; después el
                                generador reflejará esos cambios al liquidar.
                            </Typography>
                        </Stack>
                    </Paper>
                </Stack>
            </Box>
        </Layout>
    );
}
